import JSNodeConstructor from "csi/json/JSNode";
import csi from "csi/main";
import util from "csi/util";
import { DeclareConstructor } from "dojo";
import dojoDeclare from "dojo/_base/declare";
import { Action } from "./Action";
import { DefElement } from "csi/utility/defUtility";

/** mapping of action ids/tags to class constructor */
const _factoryActions: Record<string, DeclareConstructor<Action>> = {};

/** declare class and register as action */
export function declare<A extends Action, B>(id: string, superClass: DeclareConstructor<A> | null, params?: B & ThisType<DeclareConstructor & A & B>): DeclareConstructor<A & B> {
	// Be sure to ignore the generic "Action" Id.
	// Otherwise the base class will have m_id equals to "Action" and
	// Action.getId() function will always return "Action". See DE20811.
	const actionParams = params as unknown as Action;

	if (!actionParams.m_tag && !actionParams.m_id && (id != "Action")) {
		actionParams.m_id = id;
	}

	const ctor = dojoDeclare<A, B>(superClass, params);
	registerActionClass(id, ctor);
	return ctor;
}

/** register action class */
export function registerActionClass(id: string, ctor: DeclareConstructor<Action>): void {
	_factoryActions[id] = ctor;
}

/** find action definition by tag */
export function getActionDef(def: DefElement, tag: string): Action {
	return util.getDef(def, "Action[@Tag=\"" + tag + "\"]", newAction, true) || util.getDef(def, tag, newAction, true);
}

/** create action from factory based on Id or XML definition */
export function newAction(obj: string | DefElement): Action | null {
	let def: DefElement;
	let actionTypes: string[];

	if (typeof obj === "string") {
		// if string then must be in factory
		actionTypes = [obj];
	} else {
		const actionClass = obj.getAttribute("ActionClass");
		if (actionClass) {
			const ctr = csi.syncRequire(actionClass);
			if (ctr) {
				return new ctr(obj);
			}

			console.error(`newAction: Action class not resolved: ${actionClass}`);
			return null;
		}

		// when using Site_Merged.js file, all actions will use the tag Action rather than New, Copy, etc.;
		// we need to rule out all other "nodes" since properties of a tag will also be represented as tags rather than attributes
		//
		// for example, the following XML:
		//
		// <Heading Id="Dimensions" Copy="false">
		// 	<AggregateInline AggregateClass="IncrementDimension" AggregateClassId="IncrementDimension" OneRequest="1">
		// 		<Filter>
		// 			<Attribute Id="Active" Op="EQ" SValue="true" />
		// 		</Filter>
		// 		<ViewRef Id="Dimensions" URL="centric://APPAREL/SizeChartAdmin" />
		// 		<Request Module="ProductSpec" Operation="IncrementAggregateDimensions" />
		// 	</AggregateInline>
		// </Heading>
		//
		// converts to the following 3 objects after passing through selectNodes:
		// {
		//		jsNode: "Dimensions",
		//		tagName: "Id"
		// },
		// {
		//		jsNode: false,
		//		tagName: "Copy"
		// },
		// {
		//		jsNode: {...},
		//		tagName: "Action"
		// },
		if (obj instanceof JSNodeConstructor && (obj.tagName !== "Action")) {
			return null;
		}

		actionTypes = [
			obj.getAttribute("Id"),
			obj.getAttribute("Tag"),
			obj.tagName,
		];

		def = obj;
	}

	for (const type of actionTypes) {
		const ctr = _factoryActions[type];
		if (ctr) {
			return new ctr(def);
		}
	}

	return null;
}
